/**
 * Simple Language Translation System for Timeline
 * Uses direct key:value translation without nested structures
 * For English, uses the key directly instead of loading a translation file
 */
class Language {
  /**
   * Create a new Language instance
   * @param {Object} options - Configuration options
   * @param {string} options.defaultLanguage - Default language code (e.g., 'th', 'en')
   * @param {string} options.languageBasePath - Base path to language files (default: 'lang/')
   */
  constructor(options = {}) {
    this.defaultLanguage = options.defaultLanguage || 'en';
    this.currentLanguage = this.defaultLanguage;
    this.languageBasePath = options.languageBasePath || 'lang/';
    this.translations = {};
    this.translationsLoaded = new Set();
    this.onChangeCallbacks = [];
    this.isLoadingLanguage = false;
    this.loadAttempts = {};
    this.isInitializing = true;
    this.initializeLanguage();
  }

  /**
   * Initialize language system on creation
   */
  async initializeLanguage() {
    try {
      await this.setLanguage(this.defaultLanguage);
    } catch (error) {
      console.warn(`Failed to load default language (${this.defaultLanguage}): ${error.message}`);

      if (this.defaultLanguage !== 'en') {
        this.currentLanguage = 'en';
        // Don't trigger callback during initialization
        if (!this.isInitializing) {
          this._notifyLanguageChanged();
        }
      }
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Load a language file
   * @param {string} languageCode - Language code to load
   * @returns {Promise} - Promise resolving when language is loaded
   */
  async load(languageCode) {
    if (!languageCode) return Promise.reject('Invalid language code');

    if (languageCode === 'en') {
      this.translationsLoaded.add('en');
      return Promise.resolve({});
    }

    if (this.translationsLoaded.has(languageCode)) {
      return Promise.resolve(this.translations[languageCode]);
    }

    if (this.loadAttempts[languageCode]) {
      return this.loadAttempts[languageCode];
    }

    const loadPromise = new Promise(async (resolve, reject) => {
      try {
        const timestamp = Date.now();
        const url = `${this.languageBasePath}${languageCode}.json?_=${timestamp}`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to load language file for ${languageCode} (Status: ${response.status})`);
        }

        const translations = await response.json();

        this.translations[languageCode] = {
          ...(this.translations[languageCode] || {}),
          ...translations
        };

        this.translationsLoaded.add(languageCode);
        delete this.loadAttempts[languageCode];

        resolve(this.translations[languageCode]);
      } catch (error) {
        console.error(`Error loading language file for ${languageCode}:`, error);
        delete this.loadAttempts[languageCode];
        reject(error);
      }
    });

    this.loadAttempts[languageCode] = loadPromise;

    return loadPromise;
  }

  /**
   * Set the current language
   * @param {string} languageCode - Language code to set
   * @returns {Promise} - Promise resolving when language is set
   */
  async setLanguage(languageCode) {
    if (!languageCode) return Promise.reject('Invalid language code');
    if (this.isLoadingLanguage) return Promise.reject('Language change already in progress');

    this.isLoadingLanguage = true;

    try {
      if (languageCode !== 'en' && !this.translationsLoaded.has(languageCode)) {
        await this.load(languageCode);
      }
      this.currentLanguage = languageCode;
      // Don't trigger callback during initial loading
      if (!this.isInitializing) {
        this._notifyLanguageChanged();
      }
      this.isLoadingLanguage = false;

      return languageCode;
    } catch (error) {
      this.isLoadingLanguage = false;
      console.error(`Error setting language to ${languageCode}: ${error.message}`);
      return Promise.reject(error);
    }
  }

  /**
   * Translate a key
   * @param {string} key - The text key to translate
   * @param {Object} params - Parameters for interpolation
   * @returns {string} - Translated text or original key if not found
   */
  translate(key, params = {}) {
    if (!key) return '';

    if (this.currentLanguage === 'en') {
      return params && Object.keys(params).length > 0
        ? this._replaceParams(key, params)
        : key;
    }

    const translations = this.translations[this.currentLanguage];

    if (!translations) return params && Object.keys(params).length > 0
      ? this._replaceParams(key, params)
      : key;

    const translation = translations[key] || key;

    if (!params || Object.keys(params).length === 0) {
      return translation;
    }

    return this._replaceParams(translation, params);
  }

  /**
   * Short alias for translate
   */
  t(key, params = {}) {
    return this.translate(key, params);
  }

  /**
   * Replace parameters in a string
   * @param {string} text - Text with placeholders
   * @param {Object} params - Parameters to replace
   * @returns {string} - Text with replaced parameters
   * @private
   */
  _replaceParams(text, params) {
    return text.replace(/{([^{}]*)}/g, (match, key) => {
      const value = params[key];
      return typeof value !== 'undefined' ? value : match;
    });
  }

  /**
   * Register translations manually
   * @param {string} languageCode - Language code
   * @param {Object} translations - Translations object
   */
  register(languageCode, translations) {
    if (!languageCode || !translations) return;

    this.translations[languageCode] = {
      ...(this.translations[languageCode] || {}),
      ...translations
    };

    this.translationsLoaded.add(languageCode);

    if (this.currentLanguage === languageCode) {
      this._notifyLanguageChanged();
    }
  }

  /**
   * Get the current language code
   * @returns {string} - Current language code
   */
  getCurrentLanguage() {
    return this.currentLanguage;
  }

  /**
   * Get list of loaded language codes
   * @returns {Array<string>} - Array of loaded language codes
   */
  getLoadedLanguages() {
    return Array.from(this.translationsLoaded);
  }

  /**
   * Add a callback function to be called when language changes
   * @param {Function} callback - Function to call on language change
   */
  onChange(callback) {
    if (typeof callback === 'function' && !this.onChangeCallbacks.includes(callback)) {
      this.onChangeCallbacks.push(callback);
    }
  }

  /**
   * Complete initialization and trigger any pending callbacks
   * This should be called after all components are fully initialized
   */
  completeInitialization() {
    if (this.isInitializing) {
      this.isInitializing = false;

      if (this.onChangeCallbacks.length > 0) {
        this._notifyLanguageChanged();
      }
    }
  }

  /**
   * Remove a change callback
   * @param {Function} callback - Function to remove
   */
  offChange(callback) {
    this.onChangeCallbacks = this.onChangeCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Notify all registered callbacks about language change
   * @private
   */
  _notifyLanguageChanged() {
    if (this.isInitializing) return;
    this.onChangeCallbacks.forEach(callback => {
      try {
        callback(this.currentLanguage);
      } catch (error) {
        console.error('Error in language change callback:', error);
      }
    });

    const event = new CustomEvent('languageChanged', {
      detail: {language: this.currentLanguage}
    });
    document.dispatchEvent(event);
  }
}

window.Language = Language;
